/* eslint-disable */
// AudiencePicker — the three-way "who sees this" control and its one panel.
//
// Split out of AnnouncementComposer to keep both files under the 300-line cap
// (Conv #6 / #163).
//
// ⚠️ What this hides is a courtesy, not a security boundary. The database
// re-checks every audience and answers `audience_not_permitted` to a tampered
// payload (§2.4). Hiding a mode the user may not use just means they are never
// offered a button that then refuses them.

import React, { useMemo, useRef, useState } from 'react';
import styles from './composer.module.css';

const MODE_LABEL = { all: 'Everyone', portal: 'By portal', users: 'Pick people' };

function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function AudiencePicker({
  modes, mode, onModeChange,
  portalOptions, selectedPortals, onTogglePortal,
  userOptions, selectedUsers, onToggleUser,
  totalPeople,
}) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return userOptions;
    return userOptions.filter(
      (u) => u.name.toLowerCase().includes(needle) || (u.email || '').toLowerCase().includes(needle),
    );
  }, [q, userOptions]);

  // Arrow keys move, space toggles (§4.8). Kept on the list container so the
  // search box keeps its own caret behaviour.
  const onListKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      const u = filtered[cursor];
      if (u) onToggleUser(u.id);
    }
  };

  return (
    <div>
      <div className={styles.label}>Who sees this</div>

      <div className={styles.seg} role="tablist" data-test="composer-audience-modes">
        {modes.map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            className={`${styles.segBtn}${mode === m ? ' ' + styles.on : ''}`}
            onClick={() => onModeChange(m)}
            data-test={`composer-mode-${m}`}
          >
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>

      <div className={styles.panel}>
        {mode === 'all' && (
          <div className={styles.hint} data-test="composer-panel-all">
            {/* "up to" — this is a candidate count, not a delivery count (§4.4) */}
            Goes to up to <strong>{totalPeople}</strong> people in the Hub.
          </div>
        )}

        {mode === 'portal' && (
          <div className={styles.chips} data-test="composer-panel-portal">
            {portalOptions.length === 0 && (
              <div className={styles.hint}>No portals available to you.</div>
            )}
            {portalOptions.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`${styles.chip}${selectedPortals.includes(p.id) ? ' ' + styles.on : ''}`}
                onClick={() => onTogglePortal(p.id)}
                aria-pressed={selectedPortals.includes(p.id)}
                data-test={`composer-portal-${p.id}`}
              >
                {p.id}<span className={styles.chipCount}>{p.count}</span>
              </button>
            ))}
          </div>
        )}

        {mode === 'users' && (
          <div data-test="composer-panel-users">
            <input
              className={styles.search}
              value={q}
              onChange={(e) => { setQ(e.target.value); setCursor(0); }}
              placeholder="Search people…"
              aria-label="Search people"
              data-test="composer-people-search"
            />
            <div
              className={styles.people}
              ref={listRef}
              tabIndex={0}
              role="listbox"
              aria-multiselectable="true"
              onKeyDown={onListKeyDown}
            >
              {filtered.length === 0 && <div className={styles.emptyList}>No one matches “{q}”.</div>}
              {filtered.map((u, i) => {
                const on = selectedUsers.includes(u.id);
                return (
                  <div
                    key={u.id}
                    role="option"
                    aria-selected={on}
                    className={`${styles.person}${i === cursor ? ' ' + styles.active : ''}`}
                    onClick={() => { setCursor(i); onToggleUser(u.id); }}
                    data-test={`composer-person-${u.id}`}
                  >
                    <span className={`${styles.personBox}${on ? ' ' + styles.on : ''}`}>{on ? '✓' : ''}</span>
                    <span className={styles.personAvatar}>{initials(u.name)}</span>
                    <span className={styles.personName}>{u.name}</span>
                    <span className={styles.personRole}>{u.role}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AudiencePicker;
